import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, Loader2 } from 'lucide-react';
import { useCatalog, CAT_KEYS } from '../data/catalog';
import { EXPERIENCE_PARTNERS } from '../data/experiencePartners';
import { useLang } from '../i18n';
import { useSEO } from '../seo';
import { PageHeader } from '../components/PageHeader';
import { Reveal } from '../components/Reveal';
import { PublicPartnerProducts } from '../components/PublicPartnerProducts';
import { MarketplaceCard } from '../components/MarketplaceCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const durationBucket = (d) => {
  if (!d) return 'half';
  if (d.includes('Half') || d.includes('2h') || d.includes('3h') || d.includes('14:00') || d.includes('15:00') || d.includes('16:00') || d.includes('4–5h')) return 'half';
  return 'full';
};

const DEST_FILTERS = ['Todos', 'São Tomé', 'Príncipe', 'Santiago'];
const NEW_DESTS = [];

export default function ExperiencesPage() {
  const { t, lang } = useLang();
  const { experiences } = useCatalog();
  const [params, setParams] = useSearchParams();
  useSEO('Experiências | Eco Collection', 'Experiências autênticas em São Tomé, Príncipe e Santiago, Cabo Verde — preço sob consulta.');
  const [destination, setDestination] = useState(params.get('island') || 'Todos');
  const [category, setCategory] = useState('Todos');
  const [partner, setPartner] = useState('Todos');
  const [duration, setDuration] = useState('Todos');
  const [inspiration, setInspirationState] = useState(params.get('inspiration') || 'all');
  const setInspiration = (slug) => {
    setInspirationState(slug);
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (slug === 'all') next.delete('inspiration'); else next.set('inspiration', slug);
      return next;
    }, { replace: true });
  };
  const [inspirationCats, setInspirationCats] = useState([]);
  const [inspirationLoaded, setInspirationLoaded] = useState(false);

  useEffect(() => {
    fetch(`${API}/inspiration-categories`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setInspirationCats((Array.isArray(d) ? d : []).filter((c) => c.experience_slugs.length > 0)))
      .catch(() => {})
      .finally(() => setInspirationLoaded(true));
  }, []);

  // URL -> state sync (in-app nav links, back/forward)
  useEffect(() => {
    setInspirationState(params.get('inspiration') || 'all');
    setDestination(params.get('island') || 'Todos');
  }, [params]);

  const partnerOptions = useMemo(() => [...new Set(Object.values(EXPERIENCE_PARTNERS))], []);
  const categories = ['Todos', 'Natureza', 'Cultura', 'Mar', 'Gastronomia'];

  const activeInspiration = inspirationCats.find((c) => c.slug === inspiration);
  const filtered = experiences.filter((e) => {
    if (destination !== 'Todos' && e.island !== destination) return false;
    if (category !== 'Todos' && !e.cats.includes(category)) return false;
    if (partner !== 'Todos' && EXPERIENCE_PARTNERS[e.slug] !== partner) return false;
    if (duration !== 'Todos' && durationBucket(e.durationBase) !== duration) return false;
    if (activeInspiration && !activeInspiration.experience_slugs.includes(e.slug)) return false;
    return true;
  });

  const selectCls = 'rounded-full border border-eco-ink/15 bg-white px-4 py-2 text-sm text-eco-ink/70 outline-none focus:border-eco-gold';

  return (
    <div data-testid="experiences-page">
      <PageHeader kicker={t('page.experiences.k')} title={t('page.experiences.t')} sub={t('page.experiences.s')} />

      <section className="bg-eco-cream py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal className="flex flex-wrap items-center gap-3">
            <select data-testid="filter-destination" value={destination} onChange={(e) => setDestination(e.target.value)} className={selectCls} aria-label={t('filter.destination')}>
              {DEST_FILTERS.map((i) => <option key={i} value={i}>{i === 'Todos' ? `${t('filter.destination')}: ${t('filter.all')}` : i === 'Santiago' ? 'Santiago, Cabo Verde' : i}</option>)}
            </select>
            <select data-testid="filter-category" value={category} onChange={(e) => setCategory(e.target.value)} className={selectCls} aria-label={t('filter.category')}>
              {categories.map((c) => <option key={c} value={c}>{c === 'Todos' ? `${t('filter.category')}: ${t('filter.all')}` : t(CAT_KEYS[c] || c)}</option>)}
            </select>
            <select data-testid="filter-partner" value={partner} onChange={(e) => setPartner(e.target.value)} className={selectCls} aria-label={t('filter.partner')}>
              <option value="Todos">{t('filter.partner')}: {t('filter.all')}</option>
              {partnerOptions.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <select data-testid="filter-duration" value={duration} onChange={(e) => setDuration(e.target.value)} className={selectCls} aria-label={t('filter.duration')}>
              <option value="Todos">{t('filter.duration')}: {t('filter.all')}</option>
              <option value="half">{t('dur.half')}</option>
              <option value="full">{t('dur.full')}</option>
            </select>
            <span data-testid="experiences-count" className="text-sm text-eco-ink/50">{inspiration !== 'all' && !inspirationLoaded ? '…' : filtered.length}</span>
          </Reveal>

          {inspirationCats.length > 0 && (
            <Reveal className="mt-4 flex flex-wrap items-center gap-2" data-testid="inspiration-filters">
              <span className="mr-1 text-[11px] font-bold uppercase tracking-[0.14em] text-eco-ink/45">{t('insp.filter')}</span>
              <button data-testid="inspiration-chip-all" onClick={() => setInspiration('all')} className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${inspiration === 'all' ? 'bg-eco-green text-eco-gold' : 'bg-white text-eco-ink/60 hover:text-eco-ink'}`}>{t('filter.all')}</button>
              {inspirationCats.map((c) => (
                <button key={c.slug} data-testid={`inspiration-chip-${c.slug}`} onClick={() => setInspiration(c.slug)} className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${inspiration === c.slug ? 'bg-eco-green text-eco-gold' : 'bg-white text-eco-ink/60 hover:text-eco-ink'}`}>
                  {c.name[lang] || c.name.pt}
                </button>
              ))}
            </Reveal>
          )}

          {inspiration !== 'all' && !inspirationLoaded ? (
            <div className="mt-10 grid place-items-center rounded-3xl border border-dashed border-eco-ink/15 bg-white py-16" data-testid="experiences-loading">
              <Loader2 className="animate-spin text-eco-green" size={26} />
            </div>
          ) : (
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((e, i) => (
              <Reveal key={e.slug} delay={(i % 3) * 0.08}>
                <MarketplaceCard
                  experience={e}
                  to={`/experiences/${e.slug}`}
                  testid={`experiences-card-${e.slug}`}
                  image={e.img}
                  imageBadge={e.island}
                  eyebrow={`${e.island} · ${e.category}`}
                  title={e.name}
                  location={e.location}
                  duration={e.duration}
                  partner={`${t('detail.operator')}: ${EXPERIENCE_PARTNERS[e.slug] || t('detail.localop')}`}
                  cta={t('cta.availability')}
                />
              </Reveal>
            ))}
          </div>
          )}
          {filtered.length === 0 && (
            <div data-testid="experiences-empty" className="mt-10 rounded-3xl border border-dashed border-eco-ink/15 bg-white py-14 text-center">
              <p className="font-serif text-xl text-eco-ink/70">{t('empty.dest', { dest: destination })}</p>
              {NEW_DESTS.includes(destination) && (
                <Link
                  to={`/plan-my-trip?destination=${encodeURIComponent(destination)}`}
                  data-testid="experiences-empty-cta"
                  className="group mt-6 inline-flex items-center gap-2 rounded-full bg-eco-green px-7 py-3 text-sm font-semibold text-white transition-colors hover:bg-eco-deep"
                >
                  {t('nav.plan')} <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              )}
            </div>
          )}
          <PublicPartnerProducts type="experience" island={destination} />
        </div>
      </section>
    </div>
  );
}
